/**
 * Offline Storage Manager - IndexedDB Wrapper
 * Manages local storage for offline functionality
 */

const DB_NAME = 'djnomad_offline';
const DB_VERSION = 1;

const STORES = {
  EVENTS: 'events',
  PLAYLISTS: 'playlists',
  TRACKS: 'tracks',
  AUDIO_FILES: 'audio_files',
  SYNC_QUEUE: 'sync_queue'
};

class OfflineStorage {
  constructor() {
    this.db = null;
    this.isSupported = this.checkSupport();
  }

  checkSupport() {
    return 'indexedDB' in window;
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    if (!this.isSupported) {
      throw new Error('IndexedDB not supported');
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        console.log('📦 IndexedDB initialized');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Events store
        if (!db.objectStoreNames.contains(STORES.EVENTS)) {
          const eventStore = db.createObjectStore(STORES.EVENTS, { keyPath: 'id' });
          eventStore.createIndex('owner_id', 'owner_id', { unique: false });
          eventStore.createIndex('date', 'date', { unique: false });
        }

        // Playlists store
        if (!db.objectStoreNames.contains(STORES.PLAYLISTS)) {
          const playlistStore = db.createObjectStore(STORES.PLAYLISTS, { keyPath: 'id' });
          playlistStore.createIndex('event_id', 'event_id', { unique: false });
          playlistStore.createIndex('part_id', 'part_id', { unique: false });
        }

        // Tracks store
        if (!db.objectStoreNames.contains(STORES.TRACKS)) {
          const trackStore = db.createObjectStore(STORES.TRACKS, { keyPath: 'id' });
          trackStore.createIndex('playlist_id', 'playlist_id', { unique: false });
        }

        // Audio files store (blobs)
        if (!db.objectStoreNames.contains(STORES.AUDIO_FILES)) {
          db.createObjectStore(STORES.AUDIO_FILES, { keyPath: 'file_path' });
        }

        // Sync queue (for background sync)
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        console.log('📦 IndexedDB structure created');
      };
    });
  }

  /**
   * Save Event for Offline
   */
  async saveEvent(event) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.EVENTS], 'readwrite');
      const store = transaction.objectStore(STORES.EVENTS);
      const request = store.put({
        ...event,
        offline_downloaded: true,
        offline_downloaded_at: new Date().toISOString()
      });

      request.onsuccess = () => {
        console.log('✅ Event saved offline:', event.id);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save Playlist for Offline
   */
  async savePlaylist(playlist) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PLAYLISTS], 'readwrite');
      const store = transaction.objectStore(STORES.PLAYLISTS);
      const request = store.put(playlist);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save Track for Offline
   */
  async saveTrack(track) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.TRACKS], 'readwrite');
      const store = transaction.objectStore(STORES.TRACKS);
      const request = store.put(track);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save Audio File (Blob)
   */
  async saveAudioFile(file_path, blob) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.AUDIO_FILES], 'readwrite');
      const store = transaction.objectStore(STORES.AUDIO_FILES);
      const request = store.put({
        file_path,
        blob,
        cached_at: new Date().toISOString()
      });

      request.onsuccess = () => {
        console.log('🎵 Audio file cached:', file_path);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get Event from Offline Storage
   */
  async getEvent(eventId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.EVENTS], 'readonly');
      const store = transaction.objectStore(STORES.EVENTS);
      const request = store.get(eventId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get All Offline Events
   */
  async getAllEvents() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.EVENTS], 'readonly');
      const store = transaction.objectStore(STORES.EVENTS);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get Playlists for Event
   */
  async getPlaylistsByEvent(eventId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PLAYLISTS], 'readonly');
      const store = transaction.objectStore(STORES.PLAYLISTS);
      const index = store.index('event_id');
      const request = index.getAll(eventId);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get Tracks for Playlist
   */
  async getTracksByPlaylist(playlistId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.TRACKS], 'readonly');
      const store = transaction.objectStore(STORES.TRACKS);
      const index = store.index('playlist_id');
      const request = index.getAll(playlistId);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get Audio File Blob
   */
  async getAudioFile(file_path) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.AUDIO_FILES], 'readonly');
      const store = transaction.objectStore(STORES.AUDIO_FILES);
      const request = store.get(file_path);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.blob : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if Event is Available Offline
   */
  async isEventOffline(eventId) {
    const event = await this.getEvent(eventId);
    return event && event.offline_downloaded === true;
  }

  /**
   * Delete Event from Offline Storage
   */
  async deleteEvent(eventId) {
    if (!this.db) await this.init();

    // Get playlists to delete associated tracks and audio
    const playlists = await this.getPlaylistsByEvent(eventId);
    
    for (const playlist of playlists) {
      const tracks = await this.getTracksByPlaylist(playlist.id);
      
      // Delete audio files
      for (const track of tracks) {
        if (track.file_path) {
          await this.deleteAudioFile(track.file_path);
        }
      }
      
      // Delete tracks
      await this.deleteTracksByPlaylist(playlist.id);
    }
    
    // Delete playlists
    await this.deletePlaylistsByEvent(eventId);
    
    // Delete event
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.EVENTS], 'readwrite');
      const store = transaction.objectStore(STORES.EVENTS);
      const request = store.delete(eventId);

      request.onsuccess = () => {
        console.log('🗑️ Event deleted from offline:', eventId);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deletePlaylistsByEvent(eventId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PLAYLISTS], 'readwrite');
      const store = transaction.objectStore(STORES.PLAYLISTS);
      const index = store.index('event_id');
      const request = index.openCursor(eventId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteTracksByPlaylist(playlistId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.TRACKS], 'readwrite');
      const store = transaction.objectStore(STORES.TRACKS);
      const index = store.index('playlist_id');
      const request = index.openCursor(playlistId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAudioFile(file_path) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.AUDIO_FILES], 'readwrite');
      const store = transaction.objectStore(STORES.AUDIO_FILES);
      const request = store.delete(file_path);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get Storage Usage
   */
  async getStorageInfo() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage,
        quota: estimate.quota,
        usageInMB: (estimate.usage / (1024 * 1024)).toFixed(2),
        quotaInMB: (estimate.quota / (1024 * 1024)).toFixed(2),
        percentUsed: ((estimate.usage / estimate.quota) * 100).toFixed(2)
      };
    }
    return null;
  }

  /**
   * Clear All Offline Data
   */
  async clearAll() {
    if (!this.db) await this.init();

    const stores = [STORES.EVENTS, STORES.PLAYLISTS, STORES.TRACKS, STORES.AUDIO_FILES, STORES.SYNC_QUEUE];
    
    return Promise.all(
      stores.map(storeName => {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([storeName], 'readwrite');
          const store = transaction.objectStore(storeName);
          const request = store.clear();

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      })
    );
  }
}

// Export singleton instance
const offlineStorage = new OfflineStorage();
export default offlineStorage;
